import React from 'react';

/* ── Shared scales / data ────────────────────────────────────────────────── */

// Approximate Nifty 50 trailing PE by year. Pre-2021 values are standalone,
// 2021 onward are consolidated — the two are not directly comparable.
const PE_SERIES = [
  [2000, 27], [2001, 20], [2002, 14], [2003, 11], [2004, 14], [2005, 17],
  [2006, 20], [2007, 28], [2008, 18], [2009, 22], [2010, 24], [2011, 18],
  [2012, 17], [2013, 18], [2014, 20], [2015, 23], [2016, 22], [2017, 25],
  [2018, 26], [2019, 28], [2020, 30], [2021, 32], [2022, 22], [2023, 21.5],
  [2024, 22.5], [2025, 21], [2026, 20.2],
];

// Approximate year-end Nifty 50 index levels, for illustration.
const INDEX_SERIES = [
  [2000, 1300], [2001, 1100], [2002, 1100], [2003, 1880], [2004, 2080],
  [2005, 2836], [2006, 3966], [2007, 6138], [2008, 2959], [2009, 5201],
  [2010, 6134], [2011, 4624], [2012, 5905], [2013, 6304], [2014, 8283],
  [2015, 7946], [2016, 8186], [2017, 10531], [2018, 10863], [2019, 12168],
  [2020, 13982], [2021, 17354], [2022, 18105], [2023, 21731], [2024, 23644],
  [2025, 24200], [2026, 23400],
];

/* ── Figure 1: where the PE sits today ───────────────────────────────────── */

const ValuationBandChart = () => {
  const x = (v) => 60 + ((v - 15) / 15) * 600;
  const zones = [
    { from: 15, to: 20.5, label: ['Undervalued'], fill: '#e4f0e6', color: '#2f7d46' },
    { from: 20.5, to: 23, label: ['Fair'], fill: '#fdf4e3', color: '#a5701a' },
    { from: 23, to: 25, label: ['Getting', 'expensive'], fill: '#fbe8d6', color: '#bb5f21' },
    { from: 25, to: 30, label: ['Overvalued'], fill: '#f9dede', color: '#b03535' },
  ];

  return (
    <svg viewBox="0 0 700 200" className="rpt-chart-svg" role="img" aria-label="Current Nifty 50 PE of about 20.2 relative to valuation zones">
      <text x="350" y="20" textAnchor="middle" className="chart-title">Where the Nifty 50 PE sits today</text>

      {zones.map((z) => (
        <g key={z.label.join('')}>
          <rect x={x(z.from)} y="70" width={x(z.to) - x(z.from)} height="70" fill={z.fill} />
          {z.label.map((line, i) => (
            <text
              key={line}
              x={(x(z.from) + x(z.to)) / 2}
              y={z.label.length > 1 ? 100 + i * 14 : 110}
              textAnchor="middle"
              className="chart-zone-label"
              fill={z.color}
            >
              {line}
            </text>
          ))}
        </g>
      ))}

      {/* 10-year average marker */}
      <line x1={x(23.4)} y1="62" x2={x(23.4)} y2="148" stroke="#5c6b7a" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x={x(23.4)} y="52" textAnchor="middle" className="chart-annotation">10-yr avg 23.4x</text>

      {/* Current marker */}
      <line x1={x(20.2)} y1="46" x2={x(20.2)} y2="66" stroke="#1e3a5f" strokeWidth="2" />
      <polygon points={`${x(20.2) - 5},64 ${x(20.2) + 5},64 ${x(20.2)},74`} fill="#1e3a5f" />
      <text x={x(20.2)} y="38" textAnchor="middle" className="chart-annotation chart-annotation--strong">Current ~20.2x</text>

      {/* Axis */}
      <line x1="60" y1="140" x2="660" y2="140" stroke="#98a2b3" strokeWidth="1" />
      {[15, 17, 19, 21, 23, 25, 27, 29].map((t) => (
        <g key={t}>
          <line x1={x(t)} y1="140" x2={x(t)} y2="146" stroke="#98a2b3" strokeWidth="1" />
          <text x={x(t)} y="162" textAnchor="middle" className="chart-axis-label">{t}</text>
        </g>
      ))}
      <text x="350" y="184" textAnchor="middle" className="chart-axis-title">Nifty 50 trailing PE (x)</text>
    </svg>
  );
};

/* ── Figure 2: PE through market cycles ──────────────────────────────────── */

const PECycleChart = () => {
  const x = (year) => 70 + ((year - 2000) / 26) * 665;
  const y = (pe) => 345 - ((pe - 8) / 37) * 300;
  const points = PE_SERIES.map(([yr, pe]) => `${x(yr).toFixed(1)},${y(pe).toFixed(1)}`).join(' ');

  return (
    <svg viewBox="0 0 760 430" className="rpt-chart-svg" role="img" aria-label="Nifty 50 trailing PE from 2000 to 2026 across market cycles">
      <text x="380" y="22" textAnchor="middle" className="chart-title">Nifty 50 PE through market cycles (2000–2026, approximate)</text>

      {/* Valuation bands */}
      <rect x="70" y={y(45)} width="665" height={y(22) - y(45)} fill="#fbeeee" />
      <rect x="70" y={y(22)} width="665" height={y(16) - y(22)} fill="#fdf6e8" />
      <rect x="70" y={y(16)} width="665" height={y(8) - y(16)} fill="#eaf4ec" />
      <text x="82" y={y(41)} className="chart-zone-label" fill="#b03535">Expensive / caution (&gt;22)</text>
      <text x="82" y={y(19)} className="chart-zone-label" fill="#a5701a">Fair (16–22)</text>
      <text x="82" y={y(13)} className="chart-zone-label" fill="#2f7d46">Cheap (&lt;16)</text>

      {/* Grid + axes */}
      {[10, 15, 20, 25, 30, 35, 40, 45].map((t) => (
        <g key={t}>
          <line x1="70" y1={y(t)} x2="735" y2={y(t)} stroke="#e4e7ec" strokeWidth="1" />
          <text x="62" y={y(t) + 4} textAnchor="end" className="chart-axis-label">{t}</text>
        </g>
      ))}
      <line x1="70" y1="45" x2="70" y2="345" stroke="#98a2b3" strokeWidth="1" />
      <line x1="70" y1="345" x2="735" y2="345" stroke="#98a2b3" strokeWidth="1" />
      {[2000, 2005, 2010, 2015, 2020, 2025].map((yr) => (
        <text key={yr} x={x(yr)} y="365" textAnchor="middle" className="chart-axis-label">{yr}</text>
      ))}
      <text x="22" y="195" textAnchor="middle" className="chart-axis-title" transform="rotate(-90 22 195)">
        Nifty 50 trailing PE (x)
      </text>

      {/* Methodology break */}
      <line x1={x(2021)} y1="45" x2={x(2021)} y2="345" stroke="#5c6b7a" strokeWidth="1.2" strokeDasharray="5 4" />
      <text x={x(2021) + 6} y="300" className="chart-note">Apr 2021: NSE switched</text>
      <text x={x(2021) + 6} y="312" className="chart-note">standalone → consolidated EPS</text>

      {/* Series */}
      <polyline points={points} fill="none" stroke="#1e3a5f" strokeWidth="2.2" />
      {PE_SERIES.map(([yr, pe]) => (
        <circle key={yr} cx={x(yr)} cy={y(pe)} r="2.6" fill="#1e3a5f" />
      ))}

      {/* Annotations */}
      <text x={x(2007)} y={y(28) - 14} textAnchor="middle" className="chart-annotation">2007–08 peak ~28</text>
      <text x={x(2003)} y={y(11) + 18} textAnchor="middle" className="chart-annotation">2003 low ~11</text>
      <text x={x(2021) - 6} y={y(32) - 12} textAnchor="end" className="chart-annotation">2021 peak ~32–42*</text>
      <text x="735" y={y(20.2) - 26} textAnchor="end" className="chart-annotation chart-annotation--strong">Jun 2026</text>
      <text x="735" y={y(20.2) - 14} textAnchor="end" className="chart-annotation chart-annotation--strong">~20.2</text>
    </svg>
  );
};

/* ── Figure 3: index level vs PE ─────────────────────────────────────────── */

const PriceVsPEChart = () => {
  const x = (year) => 80 + ((year - 2000) / 26) * 620;
  const yL = (v) => 350 - (v / 26000) * 290;
  const yR = (v) => 350 - ((v - 10) / 24) * 290;
  const indexPts = INDEX_SERIES.map(([yr, v]) => `${x(yr).toFixed(1)},${yL(v).toFixed(1)}`).join(' ');
  const pePts = PE_SERIES.map(([yr, v]) => `${x(yr).toFixed(1)},${yR(v).toFixed(1)}`).join(' ');

  return (
    <svg viewBox="0 0 760 420" className="rpt-chart-svg" role="img" aria-label="Nifty 50 index level compared with its PE ratio, 2000 to 2026">
      <text x="380" y="22" textAnchor="middle" className="chart-title">Price keeps rising over time; PE oscillates within a band</text>

      {[0, 5000, 10000, 15000, 20000, 25000].map((t) => (
        <g key={t}>
          <line x1="80" y1={yL(t)} x2="700" y2={yL(t)} stroke="#eef0f3" strokeWidth="1" />
          <text x="72" y={yL(t) + 4} textAnchor="end" className="chart-axis-label">{t.toLocaleString('en-IN')}</text>
        </g>
      ))}
      {[10, 15, 20, 25, 30].map((t) => (
        <text key={t} x="708" y={yR(t) + 4} className="chart-axis-label" fill="#c8802a">{t}</text>
      ))}

      <line x1="80" y1="60" x2="80" y2="350" stroke="#98a2b3" strokeWidth="1" />
      <line x1="700" y1="60" x2="700" y2="350" stroke="#98a2b3" strokeWidth="1" />
      <line x1="80" y1="350" x2="700" y2="350" stroke="#98a2b3" strokeWidth="1" />
      {[2000, 2005, 2010, 2015, 2020, 2025].map((yr) => (
        <text key={yr} x={x(yr)} y="370" textAnchor="middle" className="chart-axis-label">{yr}</text>
      ))}
      <text x="20" y="205" textAnchor="middle" className="chart-axis-title" transform="rotate(-90 20 205)">Nifty 50 index level</text>
      <text x="746" y="205" textAnchor="middle" className="chart-axis-title" fill="#c8802a" transform="rotate(90 746 205)">Nifty 50 PE (x)</text>

      <polyline points={indexPts} fill="none" stroke="#1e3a5f" strokeWidth="2.2" />
      <polyline points={pePts} fill="none" stroke="#e09b3d" strokeWidth="2" />

      {/* Legend */}
      <g>
        <line x1="100" y1="46" x2="126" y2="46" stroke="#1e3a5f" strokeWidth="2.5" />
        <text x="132" y="50" className="chart-legend">Nifty 50 level (approx. year-end)</text>
        <line x1="352" y1="46" x2="378" y2="46" stroke="#e09b3d" strokeWidth="2.5" />
        <text x="384" y="50" className="chart-legend">Nifty 50 PE</text>
      </g>

      <text x="380" y="398" textAnchor="middle" className="chart-note">
        Index values are approximate year-end levels for illustration.
      </text>
    </svg>
  );
};

/* ── Figure 4: entry PE vs forward return ────────────────────────────────── */

const ForwardReturnChart = () => {
  const bands = [
    { label: '< 14', val: 21, fill: '#2f7d46' },
    { label: '14–18', val: 15, fill: '#6aab5f' },
    { label: '18–22', val: 11.5, fill: '#e0a33a' },
    { label: '22–24', val: 8, fill: '#dd7f33' },
    { label: '> 24', val: 7, fill: '#c9453c' },
  ];
  const y = (v) => 300 - (v / 25) * 250;
  const slot = (660 - 70) / bands.length;

  return (
    <svg viewBox="0 0 700 380" className="rpt-chart-svg" role="img" aria-label="Historical five-year forward returns by entry PE band">
      <text x="350" y="22" textAnchor="middle" className="chart-title">Lower entry PE has historically meant higher forward returns</text>

      {[0, 5, 10, 15, 20, 25].map((t) => (
        <g key={t}>
          <line x1="70" y1={y(t)} x2="660" y2={y(t)} stroke="#eef0f3" strokeWidth="1" />
          <text x="62" y={y(t) + 4} textAnchor="end" className="chart-axis-label">{t}%</text>
        </g>
      ))}
      <line x1="70" y1="50" x2="70" y2="300" stroke="#98a2b3" strokeWidth="1" />
      <line x1="70" y1="300" x2="660" y2="300" stroke="#98a2b3" strokeWidth="1" />

      {bands.map((b, i) => {
        const bx = 70 + i * slot + (slot - 74) / 2;
        return (
          <g key={b.label}>
            <rect x={bx} y={y(b.val)} width="74" height={300 - y(b.val)} fill={b.fill} rx="2" />
            <text x={bx + 37} y={y(b.val) - 8} textAnchor="middle" className="chart-value-label">~{b.val}%</text>
            <text x={bx + 37} y="320" textAnchor="middle" className="chart-axis-label">{b.label}</text>
          </g>
        );
      })}

      <text x="365" y="342" textAnchor="middle" className="chart-axis-title">Entry PE band</text>
      <text x="22" y="175" textAnchor="middle" className="chart-axis-title" transform="rotate(-90 22 175)">
        Typical subsequent 5-year CAGR
      </text>
      <text x="350" y="366" textAnchor="middle" className="chart-note">
        Illustrative historical averages, not a forecast. Past relationships need not repeat.
      </text>
    </svg>
  );
};

/* ── Report ──────────────────────────────────────────────────────────────── */

const STATS = [
  { value: '~20.2x', label: 'Current Nifty 50 PE (consolidated, TTM)' },
  { value: '23.4x', label: '10-year average PE' },
  { value: '~23,400', label: 'Nifty 50 level (mid-Jun 2026)' },
  { value: '1.23%', label: 'Dividend yield (vs 1.26% avg)' },
];

const MarketPEReport = () => (
  <article>
    {/* Cover */}
    <header className="rpt-cover">
      <p className="rpt-cover-eyebrow">Market Valuation Research</p>
      <h1 className="rpt-cover-title">
        Indian Stock Market:
        <br />
        A Valuation Analysis Through the Lens of Market PE
      </h1>
      <hr className="rpt-cover-divider" />
      <p className="rpt-cover-sub">
        Tracking historical Nifty 50 price-to-earnings, the PE–return relationship, and what current
        valuations imply for market direction.
      </p>

      <div className="rpt-stat-grid">
        {STATS.map((s) => (
          <div className="rpt-stat" key={s.label}>
            <span className="rpt-stat-value">{s.value}</span>
            <span className="rpt-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      <p className="rpt-cover-meta">
        Prepared: 16 June 2026 &nbsp;|&nbsp; Index: NSE Nifty 50 (with BSE Sensex reference)
        &nbsp;|&nbsp; Basis: trailing twelve-month (TTM) consolidated earnings
      </p>
      <p className="rpt-cover-note">
        This document is an educational market-analysis report. It is not investment advice and not
        a recommendation to buy or sell any security.
      </p>
    </header>

    {/* Executive summary */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">Executive summary</h2>
      <p>
        The market PE — the price investors pay for each rupee of company earnings — is the most
        widely watched gauge of whether Indian equities are cheap or expensive. As of mid-June 2026
        the Nifty 50 trades at roughly <strong>20.0–20.4 times</strong> trailing consolidated
        earnings, with the BSE Sensex around <strong>76,260</strong> and the Nifty 50 near{' '}
        <strong>23,400</strong>. That PE sits modestly <strong>below</strong> the 10-year average of
        about 23.4 and close to the long-run (since-2003) median of roughly 21.
      </p>
      <p>
        In plain terms, the Indian market today is in <strong>fair-to-slightly-cheap territory</strong>{' '}
        by its own historical standards — neither the euphoric overvaluation seen in 2021, nor the
        deep-value panic of 2008–2009 or March 2020. The market has spent the past year digesting a
        sharp correction, which has pulled valuations back toward more comfortable levels.
      </p>
      <p>
        History offers a clear, if probabilistic, signal: the entry PE has been strongly{' '}
        <strong>inversely</strong> related to the returns earned over the following three-to-five
        years. Buying near today's ~20x has historically been associated with{' '}
        <strong>mid-single-digit to low-double-digit</strong> annual returns over five years — decent,
        not spectacular. The data does <strong>not</strong> support the timing of short-term tops or
        bottoms.
      </p>

      <div className="rpt-callout rpt-callout--bordered">
        <h3 className="rpt-callout-title">Bottom line</h3>
        <p>
          At ~20x earnings the Nifty 50 is reasonably valued, leaning slightly cheap versus its
          decade average. The valuation backdrop is{' '}
          <strong>constructive for long-term accumulation</strong> and argues against expecting
          outsized returns or a sharp valuation-driven crash from here. Direction over the next 12–18
          months will be set mainly by <strong>earnings delivery</strong> (Q1 FY27 results onward),
          interest-rate and global-risk developments — not by the PE alone.
        </p>
      </div>

      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper rpt-chart-wrapper--wide"><ValuationBandChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 1. Current Nifty 50 PE relative to broad valuation zones and its 10-year average.
        </figcaption>
      </figure>
    </section>

    {/* 1 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">1. What the market PE measures — and a crucial caveat</h2>
      <p>
        The Nifty 50 PE is the index level divided by the combined per-share earnings of its 50
        constituent companies. A PE of 20 means investors are paying about ₹20 today for every ₹1 of
        the index's annual earnings. A higher number signals optimism (or expensiveness); a lower
        number signals caution (or value). The National Stock Exchange computes it on a
        trailing-twelve-month (TTM) basis.
      </p>

      <h3 className="rpt-subsection-heading">The standalone-to-consolidated break (April 2021)</h3>
      <p>
        Any honest historical comparison must flag one methodology change. In April 2021 the NSE
        switched the official Nifty PE calculation from standalone earnings to consolidated earnings
        (which include subsidiary profits). Because consolidated profits are larger, the published PE
        dropped overnight from roughly 40 to about 32 — not because the market got cheaper, but
        because the denominator changed. The practical consequence:{' '}
        <strong>a PE of ~20 today is not directly comparable to a PE of ~20 in, say, 2018.</strong>{' '}
        Charts that splice the two series together (including the long-run chart in this report)
        should be read with that discontinuity in mind.
      </p>

      <h3 className="rpt-subsection-heading">Reading the PE alongside other gauges</h3>
      <p>The PE is most reliable when corroborated by other valuation metrics. Two are worth watching today:</p>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>Price-to-book (P/B):</strong> about 3.07, below the 10-year average of ~3.62 — also pointing to fair-to-cheap rather than expensive.</li>
        <li><strong>Dividend yield:</strong> about 1.23%, near its long-run average of ~1.26%, in a neutral zone. Yields above ~1.5% have historically coincided with cheaper markets; below ~1% with expensive ones.</li>
      </ul>
      <p>
        When PE, P/B and dividend yield all point the same way, the valuation signal is far more
        trustworthy than any one of them alone. Today all three sit in a broadly
        neutral-to-slightly-cheap band, which reinforces the central message rather than
        contradicting it.
      </p>
    </section>

    {/* 2 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">2. Historical PE and price fluctuation</h2>
      <p>
        Indian equity valuations move in long cycles driven by earnings, liquidity, interest rates
        and sentiment. The market has spent most of its history in an 18–22 PE band, punctuated by
        euphoric peaks and fear-driven troughs. The chart below traces those swings; note the
        methodology break at 2021.
      </p>

      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper rpt-chart-wrapper--wide"><PECycleChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 2. Approximate Nifty 50 trailing PE across major cycles. Pre-2021 values are on a
          standalone basis; 2021 onward are consolidated and not directly comparable.
        </figcaption>
      </figure>

      <h3 className="rpt-subsection-heading">Key valuation episodes</h3>
      <div className="rpt-table-wrapper">
        <table className="rpt-table">
          <thead>
            <tr><th>Period / event</th><th>Approx. PE</th><th>What happened</th></tr>
          </thead>
          <tbody>
            <tr><td>Dot-com peak (2000)</td><td>~27</td><td>Tech euphoria; PE then collapsed to ~11 by 2003 as the bubble unwound.</td></tr>
            <tr><td>2003–07 bull run</td><td>11 → ~28</td><td>Strong GDP growth, FII inflows and 21% five-year earnings growth lifted valuations.</td></tr>
            <tr><td>Global Financial Crisis (2008–09)</td><td>~28 → ~11–12</td><td>Panic selling crushed the PE to multi-decade lows — a generational entry point.</td></tr>
            <tr><td>2010–19 expansion</td><td>~17–28</td><td>A long grind higher in price and multiple, with periodic corrections (2011, 2016, 2018).</td></tr>
            <tr><td>COVID crash (Mar 2020)</td><td>low ~17</td><td>Price fell ~38% in weeks; PE briefly cheap before collapsing earnings inflated it.</td></tr>
            <tr><td>COVID liquidity rally (peak Feb 2021)</td><td>~32–42*</td><td>Stimulus and depressed earnings pushed the PE to a record; *standalone basis.</td></tr>
            <tr><td>Normalisation (2022–24)</td><td>~21–23</td><td>Earnings caught up; PE settled into a fair-value band on the new consolidated basis.</td></tr>
            <tr><td>2025–26 correction</td><td>~20–21</td><td>A pullback from 2024 highs pulled valuations back below the 10-year average.</td></tr>
          </tbody>
        </table>
      </div>

      <h3 className="rpt-subsection-heading">Price rises over time; PE oscillates</h3>
      <p>
        A core insight for any long-term investor: the index <strong>level</strong> trends upward
        across decades as earnings compound, while the <strong>PE</strong> swings within a band.
        Price growth is therefore driven by two engines — rising earnings (durable) and a rising
        multiple (cyclical and mean-reverting). When most of a rally comes from multiple expansion
        rather than earnings, it tends to be more fragile.
      </p>

      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper rpt-chart-wrapper--wide"><PriceVsPEChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 3. Nifty 50 level (left axis) versus PE (right axis).
        </figcaption>
      </figure>
    </section>

    {/* 3 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">3. The PE–return relationship: why entry valuation matters</h2>
      <p>
        The single most useful property of the market PE is its relationship with{' '}
        <strong>future</strong> returns. Multiple studies of Indian equities find a consistent
        inverse link between the PE at which you invest and the return you earn over the next three
        to five years — a direct expression of the idea that the price you pay determines your margin
        of safety.
      </p>
      <ul className="rpt-list rpt-list--spaced">
        <li>Entering below a PE of ~14 has historically been followed by five-year returns of roughly <strong>18–25% CAGR</strong>.</li>
        <li>Entering above ~24 has been followed by far weaker five-year returns of roughly <strong>6–10% CAGR</strong>, at times barely ahead of fixed deposits.</li>
        <li>Analysis associated with Prof. Sanjay Bakshi notes that whenever the Nifty PE exceeded <strong>22</strong>, the <strong>average three-year forward return turned negative</strong> — a sobering reminder about chasing expensive markets.</li>
      </ul>

      <figure className="rpt-figure">
        <div className="rpt-chart-wrapper rpt-chart-wrapper--wide"><ForwardReturnChart /></div>
        <figcaption className="rpt-figure-caption">
          Figure 4. Representative historical five-year forward CAGR by entry-PE band. Illustrative
          averages from valuation studies, not a forecast.
        </figcaption>
      </figure>

      <h3 className="rpt-subsection-heading">How to use this responsibly</h3>
      <p>
        The relationship is a <strong>strategic allocation guide, not a market-timing tool</strong>.
        It says little about what happens next month and everything about the odds stacked for or
        against you over a multi-year horizon. Sensible applications include leaning into equities
        (or raising SIP amounts) when valuations are low, trimming aggressive lump-sum deployment
        when they are stretched, and keeping some dry powder for corrections. It is explicitly{' '}
        <strong>not</strong> a signal to exit the market wholesale when the PE rises.
      </p>
    </section>

    {/* 4 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">4. Where the market stands today</h2>
      <p>Placing mid-2026 in this framework:</p>
      <div className="rpt-table-wrapper">
        <table className="rpt-table">
          <thead>
            <tr><th>Metric</th><th>Current</th><th>Reference</th><th>Read</th></tr>
          </thead>
          <tbody>
            <tr><td>Nifty 50 PE (TTM, consolidated)</td><td>~20.0–20.4</td><td>10-yr avg 23.4; since-2003 median ~21</td><td>Fair / slightly cheap</td></tr>
            <tr><td>PE percentile (10-yr)</td><td>Low (single digit to ~35th)</td><td>Range ~17 to ~24 over the decade</td><td>Lower half of range</td></tr>
            <tr><td>Price-to-book</td><td>~3.07</td><td>10-yr avg ~3.62</td><td>Below average</td></tr>
            <tr><td>Dividend yield</td><td>~1.23%</td><td>Long-run avg ~1.26%</td><td>Neutral</td></tr>
            <tr><td>Index vs one year ago</td><td>Down (YoY)</td><td>Off 2024 all-time highs</td><td>Correction digested</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        Every gauge tells a consistent story: after a year of correction the Indian market has moved
        from the expensive 2021 extreme back into a reasonable valuation zone, slightly on the cheap
        side of its own decade average. There is no valuation froth of the kind that historically
        precedes deep, multiple-driven bear markets — but neither is the market at the panic-cheap
        levels that have produced the very best forward returns.
      </p>
    </section>

    {/* 5 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">5. Market-direction outlook</h2>
      <p>
        What follows is a scenario-based reading of the probabilities implied by valuation history,
        not a point forecast. The PE tells us about the odds and the likely magnitude of multi-year
        returns; it cannot tell us the timing or path. Short-term direction will be dominated by
        earnings, interest rates and global risk — factors a PE chart does not capture.
      </p>

      <h3 className="rpt-subsection-heading">What valuation alone implies</h3>
      <p>
        At ~20x, the historical base rate points to moderate, mid-single to low-double-digit
        annualised returns over the next three-to-five years, with a reasonable margin of safety
        against a valuation-driven collapse. The PE is below average, so the multiple is more likely
        to act as a mild tailwind (room to re-rate upward) than a headwind, provided earnings hold up.
      </p>

      <h3 className="rpt-subsection-heading">Three scenarios (12–18 month horizon)</h3>
      <div className="rpt-table-wrapper">
        <table className="rpt-table">
          <thead>
            <tr><th>Scenario</th><th>Trigger</th><th>Likely PE path</th><th>Direction</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Constructive (base case)</td>
              <td>Earnings grow into the FY27 cycle; rates stable to easing; global risk contained.</td>
              <td>Re-rates toward 22–23</td>
              <td>Gradual uptrend</td>
            </tr>
            <tr>
              <td>Range-bound</td>
              <td>Earnings merely meet expectations; mixed global cues; FII flows choppy.</td>
              <td>Holds ~20–21</td>
              <td>Sideways, stock-pickers' market</td>
            </tr>
            <tr>
              <td>Risk-off</td>
              <td>Earnings disappointment, oil/commodity spike, geopolitical shock or a global tech sell-off.</td>
              <td>De-rates to 17–19</td>
              <td>Correction — but to value, not bubble-burst</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        The asymmetry is the key point. Because the starting PE is reasonable rather than stretched,
        even the risk-off scenario implies a correction toward fair value rather than the kind of
        50%+ valuation unwind seen when markets crash from PEs near 28–40. That relatively benign
        downside, combined with a plausible path to modest upside, is what makes the current
        valuation backdrop <strong>constructive for patient, staggered accumulation</strong>.
      </p>

      <h3 className="rpt-subsection-heading">What would change the view</h3>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>Bearish trigger:</strong> a sharp, sustained PE expansion back above ~24–25 without matching earnings growth — historically the warning zone for weak forward returns.</li>
        <li><strong>Bullish trigger:</strong> a correction that drags the PE toward ~16–17 on stable earnings, which history associates with the strongest multi-year returns.</li>
        <li><strong>Earnings is the swing factor:</strong> the PE is only half the equation. The denominator — corporate profit growth from Q1 FY27 onward — will decide whether today's ~20x is cheap in hindsight or merely fair.</li>
      </ul>
    </section>

    {/* 6 */}
    <section className="rpt-section">
      <h2 className="rpt-section-heading">6. Limitations of a PE-based view</h2>
      <ul className="rpt-list rpt-list--spaced">
        <li><strong>One input, not the whole picture.</strong> PE ignores interest rates, the earnings cycle, balance-sheet quality and growth runway. A high PE on depressed (cyclically low) earnings can be cheaper than it looks; a low PE on peak earnings can be a trap.</li>
        <li><strong>Not a timing tool.</strong> Markets can stay expensive or cheap for years. Valuation explains multi-year return odds, not next quarter's direction.</li>
        <li><strong>Composition drift.</strong> The Nifty's sector mix changes over time (more high-PE IT/FMCG/financials, fewer low-PE commodities), which can structurally lift the 'fair' PE versus history.</li>
        <li><strong>The methodology break.</strong> Standalone (pre-2021) and consolidated (post-2021) PE series are not directly comparable; cross-era comparisons need care.</li>
        <li><strong>Index-level averages hide dispersion.</strong> Within a 'fairly valued' index, some sectors and stocks can be richly or cheaply priced; large-cap, mid-cap and small-cap valuations often diverge sharply.</li>
      </ul>

      <h3 className="rpt-subsection-heading">Sources &amp; method</h3>
      <p className="rpt-fineprint">
        Valuation data compiled from publicly available Nifty 50 PE / P/B / dividend-yield trackers
        and market reports (including NSE-derived PE series published by Trendlyne, Craytheon,
        nifty-pe-ratio.com, PrimeInvestor, Wright Research, Upstox and Business Standard) as of
        mid-June 2026. Index levels reference BSE Sensex and NSE Nifty 50 quotes for the same period.
        Historical PE figures and forward-return bands are approximate, drawn from cited studies, and
        rounded for clarity; charts are illustrative.
      </p>
    </section>

    <footer className="rpt-disclaimer">
      <p>
        <strong>Important disclaimer.</strong> This report is for general information and educational
        purposes only. It is not investment, financial, legal or tax advice, and it is not a
        recommendation, offer or solicitation to buy or sell any security or to adopt any investment
        strategy. The author is not a registered investment adviser. Markets involve risk, including
        loss of principal; past performance and historical valuation relationships do not guarantee
        future results, and the forward scenarios above are illustrative possibilities, not
        predictions. Figures are approximate and may contain errors or have changed since
        publication. Please verify current data independently and consult a SEBI-registered
        investment adviser before making any investment decision.
      </p>
    </footer>
  </article>
);

export default MarketPEReport;
